import { ResponsiveDiagram, DiagramSummary } from "@th-m/blogs/components";
import { AiFactoryIcon } from "@th-m/diagram-theme/icons";
import "./strategy-map-figure.css";

export function StrategyMapFigure() {
  const nodes = [
    { id: "governing-1", label: "Financial well-being", kind: "governing" },
    { id: "governing-2", label: "Time with family", kind: "governing" },
    { id: "institution", label: "Institutional authority", kind: "external" },
    { id: "strategy", label: "Publish teaching materials", kind: "strategy" },
    { id: "subgoal-1", label: "Reuse lessons", kind: "subgoal" },
    { id: "subgoal-2", label: "Bound the workload", kind: "subgoal" },
    { id: "subgoal-3", label: "Test net benefit", kind: "subgoal" },
    { id: "customer", label: "Customers", kind: "stakeholder" },
    { id: "partner", label: "Partners", kind: "stakeholder" },
  ] as const;

  return (
    <figure id="strategy-map" className="article-figure strategy-map-figure">
      <ResponsiveDiagram
        label="Governing a strategy"
        summary={
          <DiagramSummary
            label="Governing a strategy"
            steps={[
              { label: nodes[0].label, detail: nodes[1].label, icon: "value", focal: true },
              { label: nodes[3].label, relation: "directs", icon: "strategy" },
              {
                label: "Coordinated commitments",
                detail: nodes
                  .filter((n) => n.kind === "subgoal")
                  .map((n) => n.label)
                  .join(" · "),
                relation: "coordinates",
                icon: "goal",
              },
              {
                label: nodes[2].label,
                detail:
                  "Decision rights constrain the work; customers and partners shape its context.",
                relation: "remains subject to",
                icon: "constraint",
              },
            ]}
          />
        }
      >
        <div className="strategy-map__viewport" tabIndex={0}>
          <div
            className="strategy-map"
            role="img"
            aria-label="Two values drive a strategy, which coordinates three goals while institutional authority constrains it; the bounded internal hierarchy connects to customers and partners"
          >
            <div className="strategy-map__identity" aria-hidden="true">
              <AiFactoryIcon kind="strategy" />
              <span>Strategy</span>
            </div>
            <svg
              className="strategy-map__connections"
              viewBox="0 0 720 608"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <marker
                  id="strategy-map-arrow"
                  viewBox="0 0 8 8"
                  refX="7"
                  refY="4"
                  markerWidth="7"
                  markerHeight="7"
                  orient="auto"
                >
                  <path d="M 0 0 L 8 4 L 0 8 z" />
                </marker>
              </defs>

              <rect className="strategy-map__boundary" x="12" y="8" width="696" height="424" />

              <g className="strategy-map__connection strategy-map__connection--governance">
                <path d="M 320 116 V 152 Q 320 160 328 160 H 396 Q 404 160 404 168 V 208" markerEnd="url(#strategy-map-arrow)" />
                <path d="M 528 116 V 152 Q 528 160 520 160 H 452 Q 444 160 444 168 V 208" markerEnd="url(#strategy-map-arrow)" />
                <path d="M 424 276 V 304 M 224 304 H 624" />
                <path
                  d="M 224 304 V 340 M 424 304 V 340 M 624 304 V 340"
                  markerEnd="url(#strategy-map-arrow)"
                />
              </g>

              <g className="strategy-map__connection strategy-map__connection--relational">
                <path d="M 204 242 H 320" markerEnd="url(#strategy-map-arrow)" />
                <path className="strategy-map__stakeholder-edge" d="M 320 432 V 464 Q 320 472 312 472 H 232 Q 224 472 224 480 V 508" markerEnd="url(#strategy-map-arrow)" />
                <path className="strategy-map__stakeholder-edge" d="M 528 432 V 464 Q 528 472 536 472 H 616 Q 624 472 624 480 V 508" markerEnd="url(#strategy-map-arrow)" />
              </g>
            </svg>

            <span className="strategy-map__field-label strategy-map__field-label--hierarchy">
              Internal hierarchy
            </span>
            <span className="strategy-map__field-label strategy-map__field-label--stakeholders">
              Stakeholders
            </span>

            <span className="strategy-map__relation strategy-map__relation--governance strategy-map__relation--direction">
              drive
            </span>
            <span className="strategy-map__relation strategy-map__relation--governance strategy-map__relation--coordination">
              coordinates
            </span>
            <span className="strategy-map__relation strategy-map__relation--constraint">constrains</span>
            <span className="strategy-map__relation strategy-map__relation--customer">aligns with</span>
            <span className="strategy-map__relation strategy-map__relation--partner">coordinates with</span>

            {nodes.map((node) => (
              <div
                key={node.id}
                className={`strategy-map__node strategy-map__node--${node.kind} strategy-map__node--${node.id}${node.kind !== "strategy" ? " strategy-map__node--with-icon" : ""}`}
              >
                {node.kind !== "strategy" ? (
                  <span className="strategy-map__icon" aria-hidden="true">
                    <AiFactoryIcon kind={node.kind === "external" ? "constraint" : node.kind === "stakeholder" ? "others" : node.kind === "governing" ? "value" : "goal"} />
                  </span>
                ) : null}
                <span className="strategy-map__node-label">
                  {node.kind === "stakeholder"
                    ? "Stakeholder"
                    : node.kind === "external"
                      ? "Constraint"
                      : node.kind === "governing"
                        ? "Value"
                        : node.kind === "subgoal"
                          ? "Goal"
                          : node.kind}
                </span>
                <strong>{node.label}</strong>
              </div>
            ))}
          </div>
        </div>
      </ResponsiveDiagram>
      <figcaption id="strategy-map-caption">
        Values drive the strategy; authority constrains it. The box groups the internal hierarchy;
        dashed, labeled connections link it to stakeholders. A plausible route still needs authorization
        and worthwhile consequences.
      </figcaption>
    </figure>
  );
}
